import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, ArrowLeft, ArrowRight, HelpCircle, MessageSquareText, FileText, Image as ImageIcon, Clock, Sparkles, Ban, Pencil, Trash2, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, formatDate } from "@/lib/qa";

function AttachmentList({ items }) {
    if (!items || !items.length) return null;
    return (
        <ul className="mt-4 space-y-2">
            {items.map((a, i) => (
                <li key={i}>
                    <a href={a.data_url} download={a.filename} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:border-primary/40" data-testid={`att-${i}`}>
                        {a.mime_type === "application/pdf" ? <FileText className="size-4 text-rose-500" /> : <ImageIcon className="size-4 text-sky-500" />}
                        <span className="truncate">{a.filename}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{(a.size_bytes / 1024).toFixed(0)} KB</span>
                    </a>
                </li>
            ))}
        </ul>
    );
}

export default function RequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [q, setQ] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        API.get(`/questions/${id}`).then(({ data }) => setQ(data)).catch(e => setError(apiError(e)));
    }, [id]);

    const isDraft = q?.status === "draft";
    const canCancel = q?.status === "received";
    const cannotCancelInfo = q && !canCancel && !isDraft && q.status !== "cancelled" && !q.answer;

    const doCancel = async () => {
        setBusy(true);
        try {
            const { data } = await API.patch(`/questions/${id}`, { status: "cancelled" });
            setQ(data);
            toast.success("Richiesta annullata");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    const doDeleteDraft = async () => {
        setBusy(true);
        try {
            await API.delete(`/questions/${id}`);
            toast.success("Bozza eliminata");
            navigate("/dashboard/richieste");
        } catch (e) { toast.error(apiError(e)); setBusy(false); }
    };
    const doSendDraft = async () => {
        setBusy(true);
        try {
            const { data } = await API.patch(`/questions/${id}`, { status: "received" });
            setQ(data);
            toast.success("Domanda inviata!");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    if (error) return <div className="max-w-2xl mx-auto p-10"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>;
    if (!q) return <div className="p-16 text-center"><Loader2 className="size-6 mx-auto animate-spin" /></div>;

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground">
                <Link to="/dashboard/richieste"><ArrowLeft className="size-4 mr-1" /> Le mie richieste</Link>
            </Button>
            <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">Domanda</span>
                <StatusBadge status={q.status} />
            </div>
            <h1 className="font-serif text-3xl font-semibold" data-testid="req-title">{q.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Inviata il {formatDate(q.created_at)}</p>

            {/* Draft actions */}
            {isDraft && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="draft-actions">
                    <Button asChild size="sm" variant="outline" data-testid="draft-edit">
                        <Link to={`/dashboard/nuova?draft=${q.id}`}><Pencil className="size-4 mr-1.5" /> Modifica</Link>
                    </Button>
                    <Button size="sm" onClick={doSendDraft} disabled={busy} data-testid="draft-send">
                        <Send className="size-4 mr-1.5" /> Invia
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="draft-delete">
                                <Trash2 className="size-4 mr-1.5" /> Elimina
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Eliminare questa bozza?</AlertDialogTitle>
                                <AlertDialogDescription>La bozza sarà rimossa definitivamente. Non potrai recuperarla.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction onClick={doDeleteDraft}>Elimina</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            )}

            {/* Cancel-request action or info */}
            {canCancel && (
                <div className="mt-4">
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="req-cancel-btn">
                                <Ban className="size-4 mr-1.5" /> Annulla richiesta
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Annullare questa richiesta?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    La richiesta rimarrà nel tuo storico con stato "Annullata" e non sarà più presa in carico dai professionisti.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Torna indietro</AlertDialogCancel>
                                <AlertDialogAction onClick={doCancel} disabled={busy}>Annulla richiesta</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            )}
            {cannotCancelInfo && (
                <Alert className="mt-4 border-amber-200 bg-amber-50 text-amber-900" data-testid="req-cancel-info">
                    <Info className="size-4" />
                    <AlertDescription>
                        Questa richiesta non può più essere annullata perché è già stata presa in carico.
                    </AlertDescription>
                </Alert>
            )}

            <Card className="mt-6 border-l-4 border-l-primary border-slate-200 bg-slate-50">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-3">
                        <HelpCircle className="size-4" /> La tua domanda
                    </div>
                    <p className="whitespace-pre-wrap text-slate-800 leading-relaxed">{q.body}</p>
                    <AttachmentList items={q.attachments} />
                </CardContent>
            </Card>

            {q.answer ? (
                <>
                    <Card className="mt-4 border-l-4 border-l-emerald-500 border-slate-200" data-testid="req-answer">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-3">
                                <MessageSquareText className="size-4" /> Risposta del professionista
                            </div>
                            <p className="whitespace-pre-wrap text-slate-800 leading-relaxed">{q.answer.body}</p>
                            <div className="mt-5 flex items-center gap-3 pt-4 border-t border-slate-100">
                                <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                                    {q.answer.professional_name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div>
                                    <div className="text-sm font-semibold">{q.answer.professional_name}</div>
                                    {q.answer.professional_role && <div className="text-xs text-muted-foreground">{q.answer.professional_role}</div>}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Post-answer CTA — invite user to submit another question */}
                    <div
                        data-testid="post-answer-cta"
                        className="mt-5 relative overflow-hidden rounded-2xl bg-primary text-white px-5 sm:px-8 py-6 sm:py-7 shadow-[0_16px_36px_-20px_rgba(48,131,201,0.55)]"
                    >
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_240px_at_100%_0%,rgba(245,149,71,0.32),transparent_60%)]"
                        />
                        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                            <div className="shrink-0 hidden sm:flex size-12 rounded-2xl bg-accent text-white items-center justify-center shadow-[0_8px_18px_-8px_rgba(245,149,71,0.7)]">
                                <Sparkles className="size-6" strokeWidth={2.2} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-white leading-snug">
                                    Hai un altro dubbio condominiale?
                                </h3>
                                <p className="mt-1 text-[14.5px] sm:text-base text-white/90 leading-relaxed">
                                    Puoi inviare una nuova richiesta in qualsiasi momento.
                                </p>
                            </div>
                            <Button
                                asChild
                                data-testid="post-answer-new-question"
                                className="shrink-0 w-full sm:w-auto h-12 sm:h-13 px-6 sm:px-7 rounded-full bg-accent hover:bg-[#E17F2C] text-white font-semibold shadow-[0_10px_24px_-10px_rgba(245,149,71,0.65)]"
                            >
                                <Link to="/dashboard/nuova">
                                    Fai una nuova domanda <ArrowRight className="size-4 ml-1" />
                                </Link>
                            </Button>
                        </div>
                    </div>
                </>
            ) : (
                <Card className="mt-4 border-dashed border-2 bg-amber-50/40 border-amber-200">
                    <CardContent className="py-8 text-center">
                        <Clock className="size-8 mx-auto text-amber-600" />
                        <div className="mt-3 font-semibold text-slate-800">Stiamo lavorando alla tua risposta</div>
                        <p className="text-sm text-muted-foreground mt-1">Ti scriveremo appena il professionista avrà preparato la risposta.</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
